(() => {
  const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

  function tweenScroll(el, to, ms) {
    const from = el.scrollLeft;
    const dist = to - from;
    if (Math.abs(dist) < 1) {
      el.scrollLeft = to;
      return Promise.resolve();
    }
    const start = performance.now();
    return new Promise((resolve) => {
      function frame(now) {
        const t = Math.min(1, (now - start) / ms);
        el.scrollLeft = from + dist * ease(t);
        if (t < 1) requestAnimationFrame(frame);
        else resolve();
      }
      requestAnimationFrame(frame);
    });
  }

  function headerShadow() {
    const sentinel = document.getElementById("wb-header-sentinel");
    const group = document.getElementById("header-group");
    if (!sentinel || !group) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        group.classList.toggle("is-stuck", !entry.isIntersecting);
      },
      { rootMargin: "120px 0px 0px 0px", threshold: 0 }
    );
    io.observe(sentinel);
  }

  function navDropdowns() {
    document.querySelectorAll(".wb-nav__item--has-drop").forEach((item) => {
      const link = item.querySelector(".wb-nav__link");
      const setOpen = (open) => {
        item.classList.toggle("is-open", open);
        link?.setAttribute("aria-expanded", open ? "true" : "false");
      };
      item.addEventListener("pointerenter", () => {
        document.querySelectorAll(".wb-nav__item.is-open").forEach((openItem) => {
          if (openItem !== item) {
            openItem.classList.remove("is-open");
            openItem.querySelector(".wb-nav__link")?.setAttribute("aria-expanded", "false");
          }
        });
        setOpen(true);
      });
      item.addEventListener("pointerleave", () => setOpen(false));
    });
    document.addEventListener("click", (e) => {
      if (e.target.closest(".wb-nav__item--has-drop")) return;
      document.querySelectorAll(".wb-nav__item.is-open").forEach((open) => {
        open.classList.remove("is-open");
        open.querySelector(".wb-nav__link")?.setAttribute("aria-expanded", "false");
      });
    });
  }

  function searchModal() {
    const modal = document.getElementById("wb-search");
    if (!modal) return;
    const input = modal.querySelector("input[type='search']");
    const open = () => {
      modal.classList.add("is-open");
      input?.focus();
    };
    const close = () => modal.classList.remove("is-open");
    document.querySelectorAll("[data-wb-search-open]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        open();
      });
    });
    modal.querySelector("[data-wb-search-close]")?.addEventListener("click", close);
    modal.querySelector(".wb-search__scrim")?.addEventListener("click", close);
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") close();
    });
  }

  function mobileDrawer() {
    const drawer = document.getElementById("wb-drawer");
    const burger = document.querySelector("[data-wb-drawer-open]");
    if (!drawer || !burger) return;

    // The markup ships hidden so a slow load never flashes the panel open.
    drawer.hidden = false;

    const panel = drawer.querySelector(".wb-drawer__panel");
    const focusable = () =>
      Array.from(
        panel.querySelectorAll("a[href], button:not([disabled])")
      ).filter((el) => el.offsetParent !== null);
    let lastFocused = null;

    const open = () => {
      lastFocused = document.activeElement;
      drawer.classList.add("is-open");
      burger.setAttribute("aria-expanded", "true");
      // .page-wrapper is the scroll container on this storefront, not the
      // window, so the scroll lock has to go there as well as on body.
      document.body.style.overflow = "hidden";
      const wrap = document.querySelector(".page-wrapper");
      if (wrap) wrap.style.overflow = "hidden";
      focusable()[0]?.focus();
    };

    const close = () => {
      drawer.classList.remove("is-open");
      burger.setAttribute("aria-expanded", "false");
      document.body.style.overflow = "";
      const wrap = document.querySelector(".page-wrapper");
      if (wrap) wrap.style.overflow = "";
      // Fall back to the burger. lastFocused is whatever had focus when the
      // drawer opened, which is body if the drawer was opened by script or by
      // a pointer that never focused the button, and focusing body strands the
      // keyboard at the top of the document.
      const back =
        lastFocused && document.contains(lastFocused) && lastFocused !== document.body
          ? lastFocused
          : burger;
      back.focus();
    };

    burger.addEventListener("click", (e) => {
      e.preventDefault();
      drawer.classList.contains("is-open") ? close() : open();
    });
    drawer.querySelectorAll("[data-wb-drawer-close]").forEach((el) => {
      el.addEventListener("click", close);
    });

    document.addEventListener("keydown", (e) => {
      if (!drawer.classList.contains("is-open")) return;
      if (e.key === "Escape") {
        close();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Coming back above the breakpoint with the drawer open would otherwise
    // leave the scroll locked and focus trapped in a panel nobody can see.
    // 955px, one past the CSS breakpoint that brings the nav row back. If these
    // two disagree the drawer can close while the burger is still the only nav.
    window.matchMedia("(min-width: 955px)").addEventListener("change", (e) => {
      if (e.matches && drawer.classList.contains("is-open")) close();
    });
  }

  function carousel() {
    const root = document.querySelector("[data-wb-carousel]");
    if (!root) return;
    const rail = root.querySelector("[data-wb-rail]");
    const prev = root.querySelector("[data-wb-prev]");
    const next = root.querySelector("[data-wb-next]");
    if (!rail) return;

    const originals = Array.from(rail.children);
    const n = originals.length;
    if (n < 2) return;

    for (let copy = 0; copy < 2; copy++) {
      originals.forEach((node) => rail.appendChild(node.cloneNode(true)));
    }

    const pitchOf = () => {
      const card = rail.querySelector(".wb-card");
      if (!card) return 248;
      const style = getComputedStyle(rail);
      const gap = parseFloat(style.gap || style.columnGap || "24") || 24;
      return card.getBoundingClientRect().width + gap;
    };

    const setArrowTop = () => {
      const card = rail.querySelector(".wb-card__media");
      const arrows = root.querySelectorAll(".wb-feat__arrow");
      if (!card || !arrows.length) return;
      const top = card.getBoundingClientRect().height / 2 - 23;
      arrows.forEach((a) => {
        a.style.marginTop = `${Math.max(0, top)}px`;
      });
    };

    let moving = false;
    let timer;

    const indexOf = () => Math.round(rail.scrollLeft / pitchOf());

    const normalize = () => {
      const pitch = pitchOf();
      let i = indexOf();
      if (i < n) {
        rail.scrollLeft += n * pitch;
        i += n;
      } else if (i > 2 * n - 1) {
        rail.scrollLeft -= n * pitch;
        i -= n;
      }
      return i;
    };

    const go = async (dir) => {
      if (moving) return;
      moving = true;
      const pitch = pitchOf();
      normalize();
      await tweenScroll(rail, rail.scrollLeft + dir * pitch, 340);
      normalize();
      moving = false;
      resetTimer();
    };

    const resetTimer = () => {
      clearInterval(timer);
      timer = setInterval(() => {
        if (root.matches(":hover")) return;
        go(1);
      }, 10000);
    };

    prev?.addEventListener("click", () => go(-1));
    next?.addEventListener("click", () => go(1));

    const startMiddle = () => {
      rail.scrollLeft = n * pitchOf();
      setArrowTop();
    };

    requestAnimationFrame(startMiddle);
    window.addEventListener("resize", () => {
      startMiddle();
    });
    resetTimer();
  }

  function collectionSort() {
    const sortEl = document.querySelector("[data-wb-sort]");
    if (!sortEl) return;

    sortEl.addEventListener("change", () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("page");
      const sort = sortEl.value;
      if (sort && sort !== "featured") url.searchParams.set("sort_by", sort);
      else url.searchParams.delete("sort_by");
      window.location.assign(url.toString());
    });
  }

  function openCartOnAdd() {
    document.addEventListener("shopify:cart:lines-update", (event) => {
      if (event.action !== "add") return;
      if (window.location.pathname.indexOf("/cart") === 0) return;
      const drawer = document.querySelector("theme-drawer#cart-drawer");
      const open = () => {
        if (drawer && typeof drawer.open === "function") drawer.open();
        else if (window.Shopify && window.Shopify.actions && window.Shopify.actions.openCart) {
          window.Shopify.actions.openCart();
        }
      };
      if (event.promise) {
        event.promise
          .then((result) => {
            if (result && result.detail && result.detail.didError) return;
            open();
          })
          .catch(() => {});
      } else {
        open();
      }
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    headerShadow();
    navDropdowns();
    searchModal();
    mobileDrawer();
    carousel();
    collectionSort();
    openCartOnAdd();
  });
})();
